<script>
import { mapGetters } from 'vuex';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { computed } from 'vue';

export default {
  props: {
    integrationId: {
      type: String,
      required: true,
    },
    showAddButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['delete', 'add'],
  setup(props) {
    const { integration, isHookTypeInbox, hasConnectedHooks } =
      useIntegrationHook(props.integrationId);

    const integrationIconMap = {
      webhook: 'i-ph-webhooks-logo-bold',
      dashboard_apps: 'i-ph-squares-four-bold',
      openai: 'i-ph-openai-logo-bold',
      dialogflow: 'i-ph-robot-bold',
      google_translate: 'i-ph-translate-bold',
      dyte: 'i-ph-video-camera-bold',
      leadsquared: 'i-ph-chart-line-up-bold',
      slack: 'i-ph-slack-logo-bold',
      linear: 'i-ph-kanban-bold',
      shopify: 'i-ph-shopping-bag-bold',
      notion: 'i-ph-note-bold',
      captain: 'i-ph-sparkle-bold',
    };

    const integrationIcon = computed(() => {
      return (
        integrationIconMap[props.integrationId] || 'i-ph-puzzle-piece-bold'
      );
    });

    return { integration, isHookTypeInbox, hasConnectedHooks, integrationIcon };
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
    }),
    hookHeaders() {
      return this.integration.visible_properties;
    },
    hooks() {
      if (!this.hasConnectedHooks) {
        return [];
      }
      const { hooks } = this.integration;
      return hooks.map(hook => ({
        ...hook,
        id: hook.id,
        properties: this.hookHeaders.map(property =>
          hook.settings[property] ? hook.settings[property] : '--'
        ),
      }));
    },
    description() {
      try {
        return this.$t(
          `INTEGRATION_APPS.SIDEBAR_DESCRIPTION.${this.integration.name.toUpperCase()}`,
          { installationName: this.globalConfig.installationName }
        );
      } catch {
        return this.integration.description;
      }
    },
  },
  methods: {
    inboxName(hook) {
      return hook.inbox ? hook.inbox.name : '';
    },
    goBack() {
      this.$router.push({ name: 'settings_applications' });
    },
  },
};
</script>

<template>
  <div class="multiple-hooks-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="min-height: 56px"
    >
      <div class="flex items-center gap-3">
        <button class="back-btn" @click="goBack">
          <span class="i-ph-arrow-left-bold size-4" />
          Back
        </button>
        <span style="color: #d1d5db">|</span>
        <span :class="integrationIcon" class="size-5" style="color: #111827" />
        <h1 class="text-lg font-semibold" style="color: #111827">
          {{ integration.name }}
        </h1>
        <span class="ws-count-badge">{{ hooks.length }}</span>
      </div>
      <button v-if="showAddButton" class="ws-btn" @click="$emit('add')">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('INTEGRATION_APPS.ADD_BUTTON') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc px-6 py-3">
      <p class="text-sm" style="color: #6b7280">{{ description }}</p>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Empty State -->
      <div
        v-if="!hasConnectedHooks"
        class="flex flex-col items-center justify-center py-20 text-center"
      >
        <div class="ws-empty-icon">
          <span
            :class="integrationIcon"
            class="size-10"
            style="color: #9ca3af"
          />
        </div>
        <h3 class="text-lg font-semibold mb-2" style="color: #111827">
          {{
            $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', {
              integrationId: integration.id,
            })
          }}
        </h3>
        <p class="text-sm mb-6" style="color: #6b7280">
          Add a connection to get started
        </p>
        <button v-if="showAddButton" class="ws-btn" @click="$emit('add')">
          <span class="i-ph-plus-bold size-4" />
          {{ $t('INTEGRATION_APPS.ADD_BUTTON') }}
        </button>
      </div>

      <!-- Hooks List -->
      <div v-else class="space-y-3">
        <div v-for="hook in hooks" :key="hook.id" class="hook-card group">
          <div class="flex items-center gap-4 flex-1">
            <div class="hook-icon">
              <span
                :class="integrationIcon"
                class="size-5"
                style="color: #111827"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-3 flex-wrap">
                <span
                  v-for="(property, idx) in hook.properties"
                  :key="idx"
                  class="text-sm"
                  style="color: #111827"
                >
                  {{ property }}
                </span>
              </div>
              <p
                v-if="isHookTypeInbox && inboxName(hook)"
                class="text-xs mt-1"
                style="color: #6b7280"
              >
                Inbox: {{ inboxName(hook) }}
              </p>
            </div>
          </div>
          <button
            class="action-btn-danger"
            title="Delete"
            @click="$emit('delete', hook)"
          >
            <span class="i-ph-trash-bold size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.multiple-hooks-page,
.dark .multiple-hooks-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.back-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-count-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.hook-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.15s ease;
}
.hook-card:hover {
  border-color: #d1d5db;
  background: #fafafa;
}
.hook-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-btn-danger {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #b91c1c;
  cursor: pointer;
}
.action-btn-danger:hover {
  background: #fee2e2;
}

:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}
:deep(.modal-mask label) {
  color: #111827 !important;
}
:deep(.modal-mask input:not(.reset-base)),
:deep(.modal-mask select:not(.reset-base)),
:deep(.modal-mask textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
